/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 * 
 */

import { Database } from "../database";

/**
 * Represents a client
 */ 
export class Client {
    /**
     * The ID of the client.
     */
    clientID: bigint;
    /**
     * The name of the client.
     * */
    name: string;

    /**
     * @constructor
     * @param {bigint} clientID - The ID of the client.
     * @param {string} name - The name of the client.
     */
    constructor(clientID: bigint, name: string) {
      this.clientID = clientID;
      this.name = name;
    }

    /**
     * Saves the new client to the database.
     * @async
     * @function save
     * @param {Database} database - The database object.
     * @param {string} accessToken - The access token for executing SQL statements in the database.
     * @return {Promise<void>} - A promise indicating the completion of the save operation.
     */
    async save(database: Database, accessToken: string): Promise<void> {
      const sql = `INSERT INTO Client (clientID, name) VALUES (${this.clientID},'${this.name}')`;
      await database.readWrite(accessToken, sql);
    }
    
    /**
     * Lists all clients from the database.
     * @static
     * @async
     * @function list
     * @param {Database} database - The database object.
     * @param {string} accessToken - The access token for executing SQL statements in the database.
     * @return {Promise<Client[]>} - A promise that resolves to an array of Client objects.
     */
    static async list(database: Database, accessToken: string): Promise<Client[]> {
      const clients: Client[] = [];
      if (database) {
        const sql = "SELECT * FROM Client;";
        const rows = await database.read(accessToken, sql);
        rows.forEach((row: any) => {
          const client = new Client(BigInt(row.clientID), row.name);
          clients.push(client);
        });
      }
      return clients;
    }

    /**
     * Updates the name of the current client in the database.
     * @async
     * @function updateClient
     * @param {Database} database - The database object.
     * @param {string} accessToken - The access token for executing SQL statements in the database.
     */
    async updateClient(database:Database, accessToken:string){
      const sql = `UPDATE Client SET name='${this.name}' WHERE clientID = ${this.clientID}`;
      await database.readWrite(accessToken, sql);
    }

    /**
     * Deletes the current client from the database.
     * @async
     * @function deleteClient
     * @param {Database} database - The database object.
     * @param {string} accessToken - The access token for executing SQL statements in the database.
     */
    async deleteClient(database:Database, accessToken:string){
      const sql = `DELETE FROM Client WHERE clientID = ${this.clientID}`;
       await database.readWrite(accessToken, sql);
    }

    /**
     * Deletes multiple clients from the database based on their IDs.
     * @static
     * @async
     * @function deleteClients
     * @param {Database} database - The database object.
     * @param {string} accessToken - The access token for executing SQL statements in the database.
     * @param {Array<bigint>} selectedClientIDs - An array of client IDs to delete.
     */
    static async deleteClients(database:Database, accessToken:string, selectedClientIDs: Array<bigint>){
      const sql = `DELETE FROM Client WHERE clientID IN(${selectedClientIDs.join(",")})`;
      await database.readWrite(accessToken, sql);
    }
}